import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const SellsReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, resetNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
    }

    fetchSells();
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
      data
    });
  };

  const fetchSells = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SELLS', page, {
        from: formattedStartDate,
        to: formattedEndDate
      });
      
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('SELLS_XLSX', 1, {
        from: formattedStartDate,
        to: formattedEndDate,
      });
      
      if (response) {
        const byteCharacters = atob(response);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ventas_${formattedStartDate}_${formattedEndDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        snackbar(t('reports.fileDownloadSuccess'), 'success');
      } else {
        throw new Error(t('reports.exportInvalidFormat'));
      }
    } catch (error) {
      handleError(error, snackbar);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchSells();
  };

  const handleSellClick = (sell) => {
    saveCurrentState();
    navigate('/main/view-sell', { state: { id: sell.id } });
  };

  const activeCount = useMemo(() => {
    if (!data?.content) return 0;
    return data.content.filter(sell => sell.deleted !== 1).length;
  }, [data]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                <CloudDownloadIcon 
                  onClick={downloadExcel}
                  sx={{ 
                    fontSize: 28, 
                    cursor: 'pointer',
                    color: isLoading ? 'text.disabled' : 'text.primary'
                  }}
                />
                <SearchIcon 
                  onClick={fetchSells}
                  sx={{ 
                    fontSize: 28, 
                    cursor: 'pointer',
                    color: isLoading ? 'text.disabled' : 'text.primary'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.sells')}{' '}
                      {t('reports.recordsActiveOfTotal', {
                        active: activeCount,
                        total: data.total_results || data.content.length,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.sellsTotal')} {formatCurrency(data.total || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: (data.balance || 0) > 0 ? 'warning.light' : 'success.light', color: (data.balance || 0) > 0 ? 'warning.contrastText' : 'success.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.pendingBalance')} {formatCurrency(data.balance || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.receipt')}</TableCell>
                      <TableCell>{t('reports.clientLabel')}</TableCell>
                      <TableCell>{t('common.user')}</TableCell>
                      <TableCell align="right">{t('common.total')}</TableCell>
                      <TableCell align="right">{t('common.balance')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((sell) => {
                      const isDeleted = sell.deleted === 1;
                      return (
                        <TableRow 
                          key={sell.id}
                          hover
                          onClick={() => handleSellClick(sell)}
                          sx={{ 
                            '&:hover': { cursor: 'pointer' },
                            ...(isDeleted && { 
                              backgroundColor: 'action.disabledBackground',
                              textDecoration: 'line-through',
                              opacity: 0.7
                            })
                          }}
                        >
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {formatDate2(sell.date)}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {sell.receipt || '-'}
                            {isDeleted && ` ${t('common.cancelledLabel')}`}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {sell.owner?.name || '-'}
                            {sell.pet?.name && ` (${sell.pet.name})`}
                          </TableCell>
                          <TableCell sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {sell.user?.name || '-'}
                          </TableCell>
                          <TableCell align="right" sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {formatCurrency(sell.total)}
                          </TableCell>
                          <TableCell align="right" sx={isDeleted ? { textDecoration: 'line-through' } : {}}>
                            {sell.balance > 0 && !isDeleted ? (
                              <Typography color="error.main">
                                {formatCurrency(sell.balance)}
                              </Typography>
                            ) : (
                              formatCurrency(isDeleted ? sell.balance : 0)
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSells')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SellsReport;